import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Calendar, 
  DollarSign, 
  ShoppingBag, 
  Edit, 
  Trash2, 
  Package, 
  CreditCard, 
  Clock, 
  MapPin,
  TrendingUp,
  Activity,
  Save,
  X,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, Customer, Order, Payment } from '../../services/dataService';

interface CustomerTransaction {
  id: string;
  date: Date;
  type: 'order' | 'payment';
  amount: number;
  description: string;
  orderId?: string;
  paymentMethod?: string;
  status: string;
}

const CustomerView: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { tenantData } = useAuth();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [transactions, setTransactions] = useState<CustomerTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'transactions' | 'history'>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  // Helper function to safely convert date
  const safeToDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    if (date.toDate && typeof date.toDate === 'function') return date.toDate();
    if (typeof date === 'string' || typeof date === 'number') return new Date(date);
    return new Date();
  };

  // Helper function to safely format date
  const safeFormatDate = (date: any): string => {
    try {
      const dateObj = safeToDate(date);
      return dateObj.toLocaleDateString();
    } catch (error) {
      return 'Unknown date';
    }
  };

  const loadCustomerData = useCallback(async () => {
    if (!tenantData?.id || !customerId) return;

    try {
      setLoading(true);
      setError('');

      // Load customer data from database first
      let customerData = await dataService.getById<Customer>(tenantData.id, 'customers', customerId);
      
      // If customer not found in database, check if it's an order-derived customer
      if (!customerData && customerId.startsWith('order-derived-')) {
        // Load all orders and payments to reconstruct customer data
        const allOrders = await dataService.getOrders(tenantData.id);
        const allPayments = await dataService.getPayments(tenantData.id);
        
        // Extract customer name from the ID
        const customerName = customerId.replace('order-derived-', '').replace(/-/g, ' ');
        
        // Find orders for this customer
        const customerOrders = allOrders.filter(order => 
          order.customer?.toLowerCase() === customerName.toLowerCase()
        );
        
        if (customerOrders.length > 0) {
          // Find payments for this customer
          const customerPayments = allPayments.filter(payment => {
            return payment.customer?.toLowerCase() === customerName.toLowerCase() ||
                   customerOrders.some(order => order.id === payment.orderId);
          });
          
          // Calculate stats
          const totalSpent = customerPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
          const totalOrders = customerOrders.length;
          const lastOrderDate = customerOrders.length > 0 ? 
            new Date(Math.max(...customerOrders.map(o => new Date(o.createdAt!).getTime()))) : undefined;
          const dateJoined = customerOrders.length > 0 ? 
            new Date(Math.min(...customerOrders.map(o => new Date(o.createdAt!).getTime()))) : new Date();
          
          // Determine status
          const daysSinceLastOrder = lastOrderDate ? 
            (new Date().getTime() - safeToDate(lastOrderDate).getTime()) / (1000 * 60 * 60 * 24) : Infinity;
          const status: 'active' | 'inactive' = daysSinceLastOrder <= 30 ? 'active' : 'inactive';

          // Reconstruct customer data
          customerData = {
            id: customerId,
            name: customerOrders[0].customer,
            phone: customerOrders[0].customerPhone,
            dateJoined,
            totalOrders,
            totalSpent,
            lastOrderDate,
            status,
            createdAt: dateJoined,
            updatedAt: new Date(),
            notes: ''
          };
        }
      }
      
      if (!customerData) {
        setError('Customer not found');
        return;
      }

      setCustomer(customerData);
      setEditForm({
        name: customerData.name,
        phone: customerData.phone || '',
        notes: customerData.notes || ''
      });

      // Load all orders and filter by customer
      const allOrders = await dataService.getOrders(tenantData.id);
      const customerOrders = allOrders.filter(order => 
        order.customer === customerData!.name || order.customerId === customerId
      );
      setOrders(customerOrders);

      // Load all payments and filter by customer
      const allPayments = await dataService.getPayments(tenantData.id);
      const customerPayments = allPayments.filter(payment => {
        // Match by customer name or find order that belongs to this customer
        return payment.customer === customerData!.name || 
               payment.customerId === customerId ||
               customerOrders.some(order => order.id === payment.orderId);
      });
      setPayments(customerPayments);

      // Create combined transaction history
      const combinedTransactions: CustomerTransaction[] = [];

      // Add orders as transactions
      customerOrders.forEach(order => {
        combinedTransactions.push({
          id: `order-${order.id}`,
          date: safeToDate(order.createdAt),
          type: 'order',
          amount: order.total,
          description: `Order #${order.id?.slice(-8)} - ${order.items.length} item(s)`,
          orderId: order.id,
          status: order.status
        });
      });

      // Add payments as transactions
      customerPayments.forEach(payment => {
        combinedTransactions.push({
          id: `payment-${payment.id}`,
          date: safeToDate(payment.createdAt),
          type: 'payment',
          amount: payment.amount,
          description: `Payment - ${payment.method}`,
          orderId: payment.orderId,
          paymentMethod: payment.method,
          status: payment.status
        });
      });

      // Sort transactions by date (newest first)
      combinedTransactions.sort((a, b) => b.date.getTime() - a.date.getTime());
      setTransactions(combinedTransactions);

    } catch (error) {
      console.error('Error loading customer data:', error);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [tenantData, customerId]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  const handleSaveEdit = async () => {
    if (!tenantData?.id || !customerId || !customer) return;

    // Basic validation
    if (!editForm.name.trim()) {
      setError('Customer name is required');
      return;
    }

    try {
      setError('');
      setSaving(true);
      
      // If this is an order-derived customer, create a new customer record in the database
      if (customerId.startsWith('order-derived-')) {
        const newCustomerId = await dataService.createCustomer(tenantData.id, {
          name: editForm.name.trim(),
          phone: editForm.phone.trim() || undefined,
          notes: editForm.notes.trim() || undefined,
          status: customer.status,
          totalOrders: customer.totalOrders,
          totalSpent: customer.totalSpent,
          lastOrderDate: customer.lastOrderDate,
          dateJoined: customer.dateJoined
        });
        
        // Navigate to the new customer record
        navigate(`/customers/${newCustomerId}`);
        return;
      }
      
      // For existing database customers, update normally
      await dataService.updateCustomer(tenantData.id, customerId, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim() || undefined,
        notes: editForm.notes.trim() || undefined,
        updatedAt: new Date()
      });

      setCustomer(prev => prev ? {
        ...prev,
        name: editForm.name.trim(),
        phone: editForm.phone.trim() || undefined,
        notes: editForm.notes.trim() || undefined
      } : null);

      setIsEditing(false);
      setSuccess('Customer updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating customer:', error);
      setError('Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tenantData?.id || !customerId) return;

    try {
      setError('');
      
      // If this is an order-derived customer (not in database), just navigate back
      if (customerId.startsWith('order-derived-')) {
        navigate('/customers');
        return;
      }
      
      // For existing database customers, delete normally
      await dataService.delete(tenantData.id, 'customers', customerId);
      navigate('/customers');
    } catch (error) {
      console.error('Error deleting customer:', error);
      setError('Failed to delete customer');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-lg p-8 text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/customers')}
            className="btn-primary"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 flex items-center justify-center">
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-lg p-8 text-center max-w-md">
          <User className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Customer Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The customer you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/customers')}
            className="btn-primary"
          >
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-2 sm:p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 animate-slide-in-left">
            <button
              onClick={() => navigate('/customers')}
              className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Customer' : customer.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update customer information' : 'Customer details and history'}
              </p>
            </div>
          </div>
          
          {!isEditing && (
            <div className="flex items-center space-x-2 animate-slide-in-right">
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                title="Edit Customer"
              >
                <Edit className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-md shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                title="Delete Customer"
              >
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </button>
            </div>
          )}
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
              <span className="text-green-800 dark:text-green-200">{success}</span>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Customer Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <ShoppingBag className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Orders</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{customer.totalOrders}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-100">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Spent</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">₹{customer.totalSpent.toFixed(0)}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-200">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <Calendar className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Member Since</p>
            <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white">{safeFormatDate(customer.dateJoined)}</p>
          </div>

          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-3 md:p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:scale-105 transition-all duration-500 animate-fade-in-up animation-delay-300">
            <div className="flex items-center justify-between mb-2 md:mb-4">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-4 h-4 md:w-6 md:h-6 text-white" />
              </div>
            </div>
            <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Status</p>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(customer.status)}`}>
              {customer.status}
            </span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: User },
                { id: 'orders', label: 'Orders', icon: Package },
                { id: 'transactions', label: 'Transactions', icon: CreditCard },
                { id: 'history', label: 'History', icon: Clock }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900 dark:text-white">{customer.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter phone number"
                        />
                      ) : (
                        <p className="text-gray-900 dark:text-white">{customer.phone || 'Not provided'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Member Since</label>
                      <p className="text-gray-900 dark:text-white">{safeFormatDate(customer.dateJoined)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(customer.status)}`}>
                        {customer.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                    {isEditing ? (
                      <textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        rows={3}
                        placeholder="Enter customer notes"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-white">{customer.notes || 'No notes'}</p>
                    )}
                  </div>
                  
                  {/* Edit Action Buttons */}
                  {isEditing && (
                    <div className="mt-6 flex space-x-3">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>Save Changes</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditForm({
                            name: customer.name,
                            phone: customer.phone || '',
                            notes: customer.notes || ''
                          });
                        }}
                        disabled={saving}
                        className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No orders found</h3>
                    <p className="text-gray-600 dark:text-gray-400">This customer hasn't placed any orders yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 dark:text-white">Order #{order.id}</h4>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Total: ₹{order.total?.toFixed(2)}</p>
                          <p>Date: {safeFormatDate(order.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="space-y-4">
                {transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No transactions found</h3>
                    <p className="text-gray-600 dark:text-gray-400">No transaction history available for this customer.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {transaction.type === 'order' ? (
                              <Package className="w-4 h-4 text-primary-500" />
                            ) : (
                              <CreditCard className="w-4 h-4 text-green-500" />
                            )}
                            <span className="font-semibold text-gray-900 dark:text-white">{transaction.description}</span>
                          </div>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            transaction.type === 'order' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200' : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                          }`}>
                            {transaction.type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Amount: ₹{transaction.amount?.toFixed(2)}</p>
                          <p>Date: {safeFormatDate(transaction.date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer History</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-700 dark:text-gray-300">First Order</span>
                      <span className="text-gray-900 dark:text-white">{customer.lastOrderDate ? safeFormatDate(customer.lastOrderDate) : 'No orders yet'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-700 dark:text-gray-300">Total Orders</span>
                      <span className="text-gray-900 dark:text-white">{customer.totalOrders}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <span className="text-gray-700 dark:text-gray-300">Total Spent</span>
                      <span className="text-gray-900 dark:text-white">₹{customer.totalSpent.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-[90vw] max-w-md scale-95 animate-scale-in">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Delete Customer</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete {customer.name}? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex-1"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerView; 